import { CommunityInFeed } from "client/Api/Communities/communityFeed";
import { useUserProfileStore } from "client/Features/Settings/UserProfile/userProfileStore";
import {
	ensureNotNil,
	resolveFormattedTextString,
	setTrackingId
} from "client/Misc/utils";
import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import { CommunityId } from "./CommunityFeedPage";
import { CommunityPostOptionsModel, CommunityViewModel } from "client/Api/Communities/communityPosts";
import { CommunityPostDetailsViewModel } from "client/Api/Communities/communityPostViewer";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { NewPostComposer } from "./Composer/NewPostComposer";
import { MobileCommentHostingLocation, PostList } from "./PostList";
import { AlertManager, useAlerts } from "client/Hooks";
import { PostListData, useCommunityPostListDataSource } from "./useCommunityPostListDataSource";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./CommunityFeed.module.less";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { CommunityKind } from "client/Api/Communities/community";

export type CommunityFeedProps = {
	id: CommunityId;
	communitiesInFeed: readonly CommunityInFeed[];
	options: CommunityPostOptionsModel;
	onNavigateToCommunity?: (communityId: number) => void;
	onOpenPost: (postId: number, deeplink?: "comments") => void;
	onScrollBehaviorChange?: (hasScroll: boolean) => void;
	mobileCommentHostingLocation?: MobileCommentHostingLocation;
	onNewPost: () => void;
};

export const CommunityFeed = (props: CommunityFeedProps): ReactElement => {
	const {
		communitiesInFeed,
		id,
		onNavigateToCommunity,
		onOpenPost,
		options,
		onScrollBehaviorChange,
		mobileCommentHostingLocation,
		onNewPost
	} = props;

	const alertManager: AlertManager = useAlerts();

	const postListData: PostListData = useCommunityPostListDataSource(
		id,
		PAGE_SIZE,
		alertManager
	);

	const {
		loadMore,
		reload,
		addNewPost
	} = postListData;

	const authorUserInfo: UserIdentityModel = ensureNotNil(
		useUserProfileStore(x => x.currentUserIdentity)
	);

	const afterNewPostSavedHandler = useCallback(
		(newPost: CommunityPostDetailsViewModel): void => {
			alertManager.showSuccess(
				resolveFormattedTextString({
					template: localize("_PostHasSuccessfullyBeenCreatedInName"),
					replacements: [
						{
							what: "{{Name}}",
							filler: newPost.community
								.name
						}
					]
				})
			);

			addNewPost({
				community: newPost.community,
				createdBy: newPost.createdBy,
				createdOn: newPost.createdOn,
				id: newPost.id,
				images: newPost.images,
				isEditable: newPost.isEditable,
				message: newPost.message,
				modifiedOn: newPost.modifiedOn,
				statistics: newPost.statistics,
				isFollowing: newPost.isFollowing,
				comments: {
					hasMore: false,
					items: []
				},
				hasPoll: newPost.hasPoll,
				linkedDocuments: newPost.linkedDocuments
			});

			onNewPost();
		},
		[
			addNewPost,
			alertManager,
			onNewPost
		]
	);

	const postableCommunities = useMemo(
		(): readonly CommunityViewModel[] => {
			return sortBy(
				sortBy(
					communitiesInFeed.filter(x => x.canPost),
					x => x.name
				),
				x => x.kind === CommunityKind.AllCompany
					? 0
					: 1
			);
		},
		[communitiesInFeed]
	);

	const canPost: boolean = postableCommunities.length > 0;

	return (
		<>
			{canPost && (
				<NewPostComposer
					communityId={typeof id === "number" ? id : null}
					options={options}
					author={authorUserInfo}
					communities={postableCommunities}
					onAfterSave={afterNewPostSavedHandler}
				/>
			)}
			<div
				{...setTrackingId("community-feed-post-list")}
				className={styles.postList}
			>
				<PostList
					options={options}
					onNavigateToCommunity={onNavigateToCommunity}
					onOpenPost={onOpenPost}
					postListData={postListData}
					onLoadMore={loadMore}
					onReload={reload}
					onScrollBehaviorChange={onScrollBehaviorChange}
					mobileCommentHostingLocation={mobileCommentHostingLocation}
				/>
			</div>
		</>
	);
};
CommunityFeed.displayName="CommunityFeed";

const PAGE_SIZE = 8;
